// Shared primitives + hooks for the standalone course-landing templates (1–5).
// Each template owns its palette/typography/layout; this file provides generic
// building blocks (color math, icons, stars, video modal, on-demand fonts).
import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/* --------------------------- section ordering ---------------------------- */
// The reorderable content blocks, mirrored from the admin editor's drag-and-drop.
// Sanitizes a saved order: keeps known ids, dedupes, appends any missing.
export const DEFAULT_SECTION_ORDER = ["about", "skill", "include", "instructor", "testimonial", "moneyback", "faq"];
export function orderedSections(order) {
  const clean = Array.isArray(order)
    ? order.filter((s, i, a) => DEFAULT_SECTION_ORDER.includes(s) && a.indexOf(s) === i)
    : [];
  // Insert any missing id at its canonical position (after the last preceding
  // default sibling already present) so new blocks land near where they belong.
  DEFAULT_SECTION_ORDER.forEach((id, i) => {
    if (clean.includes(id)) return;
    let pos = 0;
    DEFAULT_SECTION_ORDER.slice(0, i).forEach((before) => {
      const idx = clean.indexOf(before);
      if (idx !== -1) pos = Math.max(pos, idx + 1);
    });
    clean.splice(pos, 0, id);
  });
  return clean;
}

/* ----------------------------- color helpers ----------------------------- */
export function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return { r: 29, g: 170, b: 97 };
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
export function rgba(hex, a) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}
// Mix toward black (amt<0) or white (amt>0), amt in -1..1.
export function shade(hex, amt) {
  const { r, g, b } = hexToRgb(hex);
  const t = amt < 0 ? 0 : 255;
  const p = Math.abs(amt);
  const to = (c) => Math.round(c * (1 - p) + t * p);
  return `#${((1 << 24) + (to(r) << 16) + (to(g) << 8) + to(b)).toString(16).slice(1)}`;
}

/* -------------------------------- fonts ---------------------------------- */
// Inject a Google Fonts stylesheet once (deduped by href) so webfonts only load
// when a landing page is actually viewed.
export function useGoogleFonts(href) {
  useEffect(() => {
    if (!href) return;
    const id = "gf-" + href.replace(/[^a-z0-9]/gi, "").slice(0, 40);
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }, [href]);
}

/* --------------------------- scroll reveal ------------------------------- */
// Fires once when the element scrolls into view — used for staggered section
// reveals on the premium landing templates.
export function useInView(threshold = 0.14) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } },
      { threshold, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [seen, threshold]);
  return [ref, seen];
}

// Wrapper that fades + rises its children into view. `delay` in ms staggers.
export function Reveal({ children, delay = 0, className = "" }) {
  const [ref, seen] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-[750ms] ease-[cubic-bezier(.16,1,.3,1)] ${seen ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ------------------------------- primitives ------------------------------ */
export function Icon({ d, className = "w-4 h-4", stroke = 1.8 }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={stroke}>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

// 5-star rating with fractional fill. `color` sets the filled color.
export function Stars({ value = 0, className = "h-4 w-4", color = "#fbbf24", empty = "rgba(255,255,255,.18)" }) {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <span className="inline-flex items-center gap-0.5">
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.max(0, Math.min(1, v - i));
        return (
          <span key={i} className={`relative inline-block ${className}`}>
            <svg viewBox="0 0 24 24" className={`absolute inset-0 ${className}`} style={{ fill: empty }}><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
            <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
              <svg viewBox="0 0 24 24" className={className} style={{ fill: color }}><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
            </span>
          </span>
        );
      })}
    </span>
  );
}

// Split a title so the 2nd word can be accented (the guruai headline pattern).
export function splitTitle(title) {
  const words = (title || "").trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return { before: title || "", accent: "", after: "" };
  return { before: words[0], accent: words[1], after: words.slice(2).join(" ") };
}

/* --------------------------- skill list icons ---------------------------- */
// Selectable icon for the "What you'll learn" bullets. Keys are stored on
// landing.skill.icon; templates render the matching path via the Icon primitive.
export const SKILL_ICONS = {
  check: "M4.5 12.75l6 6 9-13.5",
  star: "M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.562.562 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z",
  bolt: "M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z",
  sparkles: "M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z",
  arrow: "M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3",
  heart: "M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z",
  fire: "M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.047 8.287 8.287 0 009 9.601a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z",
  rocket: "M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z",
  bulb: "M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18",
  shield: "M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z",
};
export const SKILL_ICON_KEYS = Object.keys(SKILL_ICONS);
export function skillIconD(key) { return SKILL_ICONS[key] || SKILL_ICONS.check; }

// Curriculum lesson icons, keyed to the admin `lesson_type` (1 Content /
// 2 Audio / 3 Video). Outline paths so they read well at ~14px.
export const LESSON_ICONS = {
  1: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  2: "M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3",
  3: "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z",
};
export function lessonIconD(t) { return LESSON_ICONS[Number(t)] || LESSON_ICONS[1]; }
// Renders the right curriculum-lesson icon (padlock when locked, otherwise the
// icon for its type) with an explicit stroke color — the shared <Icon> takes
// its color from `currentColor`, so we draw our own svg to honor `color`.
export function LessonIcon({ lesson, color, muted = "#cbd5e1", className = "h-3.5 w-3.5" }) {
  const locked = !!lesson?.locked;
  const d = locked
    ? "M12 11V7a4 4 0 00-8 0v4m-1 0h10a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6a1 1 0 011-1z"
    : lessonIconD(lesson?.lesson_type);
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke={locked ? muted : color} strokeWidth={1.9}>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

/* ------------------------------ video helpers ---------------------------- */
export function videoEmbedUrl(platform, raw) {
  let url = (raw || "").trim();
  const iframe = url.match(/src="([^"]+)"/);
  if (iframe) url = iframe[1];
  if (url.includes("youtube.com/watch")) {
    try { const v = new URL(url).searchParams.get("v"); if (v) url = `https://www.youtube.com/embed/${v}`; } catch { /* ignore */ }
  } else if (url.includes("youtu.be/")) {
    const id = url.split("youtu.be/")[1]?.split(/[?&]/)[0];
    if (id) url = `https://www.youtube.com/embed/${id}`;
  } else if (url.includes("vimeo.com/") && !url.includes("player.vimeo")) {
    const id = url.split("vimeo.com/")[1]?.split(/[?&]/)[0];
    if (id) url = `https://player.vimeo.com/video/${id}`;
  }
  return url;
}

export function usePreviewVideo() {
  const [open, setOpen] = useState(false);
  return { open, play: () => setOpen(true), close: () => setOpen(false) };
}

export function VideoModal({ video, open, onClose }) {
  if (!open || !video?.url) return null;
  const src = videoEmbedUrl(video.platform, video.url);
  const isFile = /\.(mp4|webm|ogg)(\?|$)/i.test(src);
  return (
    <div className="fixed inset-0 z-[4000] flex items-center justify-center bg-black/85 p-4 animate-[fadeIn_.2s_ease]" onClick={onClose}>
      <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" className="absolute -top-11 right-0 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20">
          <Icon d="M6 18L18 6M6 6l12 12" className="h-5 w-5" />
        </button>
        <div className="aspect-video w-full overflow-hidden rounded-2xl bg-black shadow-2xl">
          {isFile ? (
            <video src={src} controls autoPlay className="h-full w-full" />
          ) : (
            <iframe src={`${src}${src.includes("?") ? "&" : "?"}autoplay=1`} title="Course preview" className="h-full w-full" allow="autoplay; fullscreen" allowFullScreen />
          )}
        </div>
      </div>
    </div>
  );
}

// A play button rendered over a poster/gradient. Darkens the poster + shows a
// "click to watch" hint so it reads as a clickable preview that opens a popup.
export function PlayButton({ onClick, accent, size = 60, halo = false, label = "Watch preview" }) {
  return (
    <button onClick={onClick} className="group/play absolute inset-0 flex flex-col items-center justify-center gap-2.5 bg-black/20 transition hover:bg-black/35" aria-label="Play preview">
      <span className="relative flex items-center justify-center rounded-full transition group-hover/play:scale-110" style={{ width: size, height: size, background: accent, boxShadow: halo ? `0 0 0 12px ${rgba(accent, 0.2)}, 0 0 0 24px ${rgba(accent, 0.08)}` : `0 12px 30px ${rgba(accent, 0.5)}` }}>
        <svg viewBox="0 0 24 24" className="ml-1 h-6 w-6 fill-white"><path d="M8 5v14l11-7z" /></svg>
      </span>
      {label ? <span className="rounded-full bg-black/60 px-3 py-1 text-[11px] font-semibold text-white shadow backdrop-blur-sm">{label}</span> : null}
    </button>
  );
}

// A hero video card: shows the course image (poster) with a "click to watch"
// overlay; clicking plays the video INLINE in the same place. A "Big screen"
// button (top-right) opens the fullscreen popup via onExpand. When no video is
// set it just renders the poster image. `className`/`style` size the box.
export function HeroVideo({ video, poster, accent, className = "", style, onExpand, premium = false }) {
  const [playing, setPlaying] = useState(false);
  const hasVideo = video?.show !== false && !!video?.url;
  const src = hasVideo ? videoEmbedUrl(video.platform, video.url) : "";
  const isFile = /\.(mp4|webm|ogg)(\?|$)/i.test(src);
  const img = video?.thumb || poster || "";
  return (
    <div className={`relative overflow-hidden ${className}`} style={style}>
      {playing && hasVideo ? (
        isFile ? (
          <video src={src} controls autoPlay muted playsInline className="absolute inset-0 h-full w-full bg-black" />
        ) : (
          <iframe src={`${src}${src.includes("?") ? "&" : "?"}${/youtube/.test(src) ? "autoplay=1&mute=1" : /vimeo/.test(src) ? "autoplay=1&muted=1" : "autoplay=1"}`} title="Course preview" className="absolute inset-0 h-full w-full bg-black" allow="autoplay; fullscreen" allowFullScreen />
        )
      ) : img ? (
        <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${rgba(accent, 0.9)}, ${rgba(accent, 0.5)})` }} />
      )}

      {/* Play overlay: shown when a video exists, or always in `premium` mode (as a
          premium animated cue for the hero video area). */}
      {!playing && (premium || hasVideo) && (
        <div
          onClick={hasVideo ? () => setPlaying(true) : undefined}
          role={hasVideo ? "button" : undefined}
          aria-label={hasVideo ? "Play preview" : undefined}
          className={`group/pv absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-t from-black/45 via-black/10 to-transparent transition ${hasVideo ? "cursor-pointer hover:from-black/55" : "cursor-default"}`}
        >
          <span className="relative flex items-center justify-center">
            {/* pulsing rings */}
            <span className="absolute h-[4.5rem] w-[4.5rem] animate-ping rounded-full opacity-70" style={{ background: rgba(accent, 0.35) }} />
            <span className="absolute h-24 w-24 animate-ping rounded-full opacity-40" style={{ background: rgba(accent, 0.16), animationDelay: "0.6s" }} />
            <span className="absolute h-24 w-24 rounded-full" style={{ boxShadow: `inset 0 0 0 1px ${rgba(accent, 0.45)}` }} />
            {/* main button */}
            <span className="relative flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-full text-white shadow-xl transition-transform duration-300 group-hover/pv:scale-110" style={{ background: `linear-gradient(135deg, ${accent}, ${shade(accent, -0.22)})`, boxShadow: `0 14px 36px ${rgba(accent, 0.55)}` }}>
              <svg viewBox="0 0 24 24" className="ml-1 h-8 w-8 fill-white drop-shadow"><path d="M8 5v14l11-7z" /></svg>
            </span>
          </span>
          {hasVideo && <span className="rounded-full bg-black/55 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">Click to watch</span>}
        </div>
      )}

      {hasVideo && onExpand && (
        <button onClick={(e) => { e.stopPropagation(); onExpand(); }} title="Watch on the big screen" className="absolute right-2 top-2 z-10 flex items-center gap-1.5 rounded-lg bg-black/55 px-2.5 py-1.5 text-[11px] font-semibold text-white backdrop-blur-sm transition hover:bg-black/80">
          <Icon d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" className="h-3.5 w-3.5" /> Big screen
        </button>
      )}
    </div>
  );
}

/* --------------------------- guarantee badge ----------------------------- */
// 10 premium, visually-distinct money-back seals. Each variant is a full layout
// archetype (seal / shield / medal / hexagon / minimal) + a fill scheme, fully
// theme-driven, with the day number rendered as live <text> for any value.
export const BADGE_COUNT = 10;
export const BADGE_VARIANTS = {
  1: { layout: "seal", border: "gear", fill: "solid" },
  2: { layout: "shield", fill: "gradient" },
  3: { layout: "medal", border: "gear", fill: "solid" },
  4: { layout: "seal", border: "rays", fill: "gradient" },
  5: { layout: "minimal", fill: "outline" },
  6: { layout: "seal", border: "scallop", fill: "solid" },
  7: { layout: "hexagon", fill: "solid" },
  8: { layout: "seal", border: "ticks", fill: "dark" },
  9: { layout: "medal", border: "rays", fill: "gradient" },
  10: { layout: "minimal", fill: "solid" },
};

// Decorative seal border around (cx,cy); `s` scales the radii (for smaller medallions).
function badgeBorder(border, cx, cy, edge, s = 1) {
  if (border === "ring") {
    return (<>
      <circle cx={cx} cy={cy} r={95 * s} fill="none" stroke={edge} strokeWidth={6 * s} />
      <circle cx={cx} cy={cy} r={86 * s} fill="none" stroke={edge} strokeWidth={2 * s} />
    </>);
  }
  if (border === "scallop") {
    const n = 26, out = [];
    for (let i = 0; i < n; i++) { const a = (2 * Math.PI / n) * i; out.push(<circle key={i} cx={(cx + 90 * s * Math.cos(a)).toFixed(1)} cy={(cy + 90 * s * Math.sin(a)).toFixed(1)} r={6.5 * s} fill={edge} />); }
    return <>{out}</>;
  }
  if (border === "ticks") {
    const n = 40, out = [];
    for (let i = 0; i < n; i++) { const a = (2 * Math.PI / n) * i; out.push(<line key={i} x1={(cx + 85 * s * Math.cos(a)).toFixed(1)} y1={(cy + 85 * s * Math.sin(a)).toFixed(1)} x2={(cx + 96 * s * Math.cos(a)).toFixed(1)} y2={(cy + 96 * s * Math.sin(a)).toFixed(1)} stroke={edge} strokeWidth={3 * s} strokeLinecap="round" />); }
    return <>{out}</>;
  }
  let spikes = 20, ro = 97, ri = 85;
  if (border === "rays") { spikes = 36; ro = 99; ri = 88; }
  let pts = "";
  for (let i = 0; i < spikes * 2; i++) { const r = (i % 2 === 0 ? ro : ri) * s; const a = (Math.PI / spikes) * i - Math.PI / 2; pts += `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)} `; }
  return <polygon points={pts.trim()} fill={edge} />;
}

function badgeStar(sx, sy, r) {
  let p = "";
  for (let i = 0; i < 10; i++) { const rr = i % 2 === 0 ? r : r * 0.45; const a = (Math.PI / 5) * i - Math.PI / 2; p += `${(sx + rr * Math.cos(a)).toFixed(1)},${(sy + rr * Math.sin(a)).toFixed(1)} `; }
  return p.trim();
}

export function GuaranteeBadge({ variant = 1, days = 30, accent = "#1daa61", className = "", style }) {
  const cfg = BADGE_VARIANTS[variant] || BADGE_VARIANTS[1];
  const A = accent;
  const light = shade(A, 0.62), dark = shade(A, -0.24), deep = shade(A, -0.66);
  const cx = 100, cy = 100;
  const font = "'Sora',system-ui,sans-serif";
  const uid = `gb-${variant}-${String(days).replace(/\D/g, "") || "x"}`;
  const grad = cfg.fill === "gradient", outline = cfg.fill === "outline", isDark = cfg.fill === "dark", solid = cfg.fill === "solid";
  const disc = grad ? `url(#${uid}-g)` : outline ? "#ffffff" : isDark ? deep : A;
  const onDisc = outline ? shade(A, -0.06) : "#ffffff";
  const ring = outline ? rgba(A, 0.42) : light;
  const nf = String(days).length >= 3 ? 36 : 50;

  const wrap = (kids, extraDefs) => (
    <svg viewBox="0 0 200 200" className={className} style={style} xmlns="http://www.w3.org/2000/svg" role="img" aria-label={`${days}-day money-back guarantee`}>
      <defs>
        {grad && <linearGradient id={`${uid}-g`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={A} /><stop offset="1" stopColor={dark} /></linearGradient>}
        {extraDefs}
      </defs>
      {kids}
    </svg>
  );
  const ribbon = (y) => (<>
    <path d={`M18,${y + 13} L36,${y + 3} L36,${y + 21} Z`} fill={dark} />
    <path d={`M182,${y + 13} L164,${y + 3} L164,${y + 21} Z`} fill={dark} />
    <rect x="32" y={y} width="136" height="26" rx="3" fill={solid ? light : A} />
    <text x={cx} y={y + 18} textAnchor="middle" fill={solid ? dark : "#fff"} fontFamily={font} fontSize="16.5" fontWeight="800" letterSpacing="1.5">GUARANTEE</text>
  </>);

  // ---- Minimal: clean concentric rings, no spikes ----
  if (cfg.layout === "minimal") {
    return wrap(<>
      <circle cx={cx} cy={cy} r="93" fill={disc} />
      <circle cx={cx} cy={cy} r="93" fill="none" stroke={ring} strokeWidth={outline ? 3 : 2} />
      <circle cx={cx} cy={cy} r="80" fill="none" stroke={ring} strokeWidth="1.5" />
      <text fill={onDisc} fontFamily={font} fontSize="12" fontWeight="700" letterSpacing="3.5"><textPath href={`#${uid}-t`} startOffset="50%" textAnchor="middle">MONEY-BACK GUARANTEE</textPath></text>
      <text x={cx} y={cy + 8} textAnchor="middle" fill={onDisc} fontFamily={font} fontSize={String(days).length >= 3 ? 42 : 58} fontWeight="800">{days}</text>
      <text x={cx} y={cy + 30} textAnchor="middle" fill={onDisc} fontFamily={font} fontSize="15" fontWeight="700" letterSpacing="5">DAYS</text>
    </>, <path id={`${uid}-t`} d="M38,100 a62,62 0 1,1 124,0" fill="none" />);
  }

  // ---- Hexagon ----
  if (cfg.layout === "hexagon") {
    const hex = (r) => { let p = ""; for (let k = 0; k < 6; k++) { const a = (-90 + 60 * k) * Math.PI / 180; p += `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)} `; } return p.trim(); };
    return wrap(<>
      <polygon points={hex(94)} fill={disc} />
      <polygon points={hex(94)} fill="none" stroke={ring} strokeWidth="2" />
      <polygon points={hex(80)} fill="none" stroke={ring} strokeWidth="1.5" />
      <text x={cx} y="60" textAnchor="middle" fill={onDisc} fontFamily={font} fontSize="12.5" fontWeight="800" letterSpacing="2.5">MONEY BACK</text>
      <polygon points={badgeStar(72, 94, 5.5)} fill={onDisc} />
      <polygon points={badgeStar(128, 94, 5.5)} fill={onDisc} />
      <text x={cx} y="114" textAnchor="middle" fill={onDisc} fontFamily={font} fontSize={nf} fontWeight="800">{days}</text>
      <text x={cx} y="134" textAnchor="middle" fill={onDisc} fontFamily={font} fontSize="13.5" fontWeight="800" letterSpacing="3">DAYS</text>
      <text x={cx} y="158" textAnchor="middle" fill={onDisc} fontFamily={font} fontSize="12.5" fontWeight="800" letterSpacing="2.5">GUARANTEE</text>
    </>);
  }

  // ---- Shield ----
  if (cfg.layout === "shield") {
    return wrap(<>
      <path d="M100,16 L172,44 L172,104 C172,150 138,178 100,188 C62,178 28,150 28,104 L28,44 Z" fill={disc} />
      <path d="M100,28 L162,52 L162,102 C162,142 132,166 100,175 C68,166 38,142 38,102 L38,52 Z" fill="none" stroke={ring} strokeWidth="2" />
      <text x={cx} y="62" textAnchor="middle" fill={onDisc} fontFamily={font} fontSize="13" fontWeight="800" letterSpacing="2">MONEY BACK</text>
      <polygon points={badgeStar(74, 88, 5)} fill={onDisc} />
      <polygon points={badgeStar(126, 88, 5)} fill={onDisc} />
      <text x={cx} y="116" textAnchor="middle" fill={onDisc} fontFamily={font} fontSize={nf} fontWeight="800">{days}</text>
      <text x={cx} y="137" textAnchor="middle" fill={onDisc} fontFamily={font} fontSize="15" fontWeight="800" letterSpacing="3">DAYS</text>
      <text x={cx} y="162" textAnchor="middle" fill={onDisc} fontFamily={font} fontSize="13" fontWeight="800" letterSpacing="2">GUARANTEE</text>
    </>);
  }

  // ---- Medal with ribbon tails ----
  if (cfg.layout === "medal") {
    const mcy = 82, ms = 0.78, mr = 64;
    return wrap(<>
      <path d="M72,150 L72,197 L100,183 L100,150 Z" fill={A} />
      <path d="M128,150 L128,197 L100,183 L100,150 Z" fill={dark} />
      {badgeBorder(cfg.border || "gear", cx, mcy, A, ms)}
      <circle cx={cx} cy={mcy} r={mr} fill={disc} />
      <circle cx={cx} cy={mcy} r={mr} fill="none" stroke={ring} strokeWidth="2" />
      <text fill={onDisc} fontFamily={font} fontSize="11.5" fontWeight="800" letterSpacing="2"><textPath href={`#${uid}-m`} startOffset="50%" textAnchor="middle">MONEY BACK</textPath></text>
      <polygon points={badgeStar(70, mcy - 2, 4.5)} fill={onDisc} />
      <polygon points={badgeStar(130, mcy - 2, 4.5)} fill={onDisc} />
      <text x={cx} y={mcy + 10} textAnchor="middle" fill={onDisc} fontFamily={font} fontSize={String(days).length >= 3 ? 30 : 42} fontWeight="800">{days}</text>
      <text x={cx} y={mcy + 28} textAnchor="middle" fill={onDisc} fontFamily={font} fontSize="12.5" fontWeight="800" letterSpacing="3">DAYS</text>
    </>, <path id={`${uid}-m`} d={`M52,${mcy} a48,48 0 1,1 96,0`} fill="none" />);
  }

  // ---- Seal (default): decorative border + arched text + ribbon ----
  return wrap(<>
    {badgeBorder(cfg.border, cx, cy, A)}
    <circle cx={cx} cy={cy} r="82" fill={disc} />
    <circle cx={cx} cy={cy} r="82" fill="none" stroke={ring} strokeWidth={outline ? 3 : 2} />
    <text fill={onDisc} fontFamily={font} fontSize="18" fontWeight="800" letterSpacing="3"><textPath href={`#${uid}-arc`} startOffset="50%" textAnchor="middle">MONEY BACK</textPath></text>
    <circle cx={cx} cy={cy} r="53" fill="none" stroke={ring} strokeWidth="2.5" />
    <polygon points={badgeStar(63, 95, 6)} fill={onDisc} />
    <polygon points={badgeStar(137, 95, 6)} fill={onDisc} />
    <text x={cx} y="106" textAnchor="middle" fill={onDisc} fontFamily={font} fontSize={nf} fontWeight="800">{days}</text>
    <text x={cx} y="128" textAnchor="middle" fill={onDisc} fontFamily={font} fontSize="18" fontWeight="800" letterSpacing="4">DAYS</text>
    {ribbon(137)}
  </>, <path id={`${uid}-arc`} d="M34,100 a66,66 0 1,1 132,0" fill="none" />);
}

/* ---------------------------- policy links ------------------------------- */
// Footer "Privacy Policy" / "Terms" links that open the community's content in a
// popup. `policies` is { privacy, terms } (HTML) from the course-detail API.
export function PolicyLinks({ policies, accent = "#2CCD4F", dark = false, className = "" }) {
  const [open, setOpen] = useState(null);
  const items = [
    policies?.privacy && { key: "privacy", label: "Privacy Policy", html: policies.privacy },
    policies?.terms && { key: "terms", label: "Terms", html: policies.terms },
  ].filter(Boolean);
  if (!items.length) return null;
  const cur = items.find((i) => i.key === open);
  return (
    <div className={`flex items-center gap-4 sm:gap-5 ${className}`}>
      {items.map((it, i) => (
        <Fragment key={it.key}>
          {i > 0 && <span className="h-1 w-1 rounded-full opacity-40" style={{ background: "currentColor" }} />}
          <button type="button" onClick={() => setOpen(it.key)} className="group inline-flex items-center gap-1.5 text-sm font-medium transition hover:opacity-100" style={{ color: accent }}>
            <span className="border-b border-transparent transition-colors group-hover:border-current">{it.label}</span>
          </button>
        </Fragment>
      ))}
      {cur && <PolicyModal title={cur.label} html={cur.html} kind={cur.key} accent={accent} dark={dark} onClose={() => setOpen(null)} />}
    </div>
  );
}
// Icon glyphs for the two policy kinds (shield for privacy, document for terms).
const POLICY_ICON = {
  privacy: "M9 12.75 11.25 15 15 9.75m-3-7.036A11.96 11.96 0 0 1 3.6 6 12 12 0 0 0 3 9.75c0 5.59 3.82 10.29 9 11.62 5.18-1.33 9-6.03 9-11.62 0-1.31-.21-2.57-.6-3.75h-.15c-3.2 0-6.1-1.25-8.25-3.29Z",
  terms: "M19.5 14.25v-2.63a3.38 3.38 0 0 0-3.38-3.37h-1.5a1.12 1.12 0 0 1-1.12-1.13v-1.5a3.38 3.38 0 0 0-3.38-3.37H8.25m2.25 0H5.63c-.62 0-1.13.5-1.13 1.12v17.25c0 .62.5 1.13 1.13 1.13h12.75c.62 0 1.12-.5 1.12-1.13V11.25a9 9 0 0 0-9-9Z",
};
function PolicyModal({ title, html, kind = "privacy", accent = "#2CCD4F", dark = false, onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  // Theme-matched surface palette — mirrors the parent template (dark vs light).
  const surface = dark ? "#0e1322" : "#ffffff";
  const surface2 = dark ? "#161d31" : "#f7f8fb";
  const ink = dark ? "#f4f6fb" : "#0b1220";
  const body = dark ? "#c3cbdc" : "#3b4453";
  const subtle = dark ? "#8b93a7" : "#6b7280";
  const border = dark ? rgba("#ffffff", 0.08) : rgba("#0b1220", 0.08);
  const grad = `linear-gradient(135deg, ${accent}, ${shade(accent, -0.22)})`;

  return createPortal(
    <div
      className="fixed inset-0 z-[5000] flex items-end justify-center p-0 sm:items-center sm:p-4 animate-[fadeIn_.2s_ease]"
      style={{ background: dark ? rgba("#05070d", 0.74) : rgba("#0b1220", 0.5), backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
      onMouseDown={onClose}
    >
      <style>{`
        @keyframes policyIn{from{opacity:0;transform:translateY(24px) scale(.97)}to{opacity:1;transform:none}}
        .policy-scroll{scrollbar-width:thin;scrollbar-color:${rgba(accent, 0.5)} transparent}
        .policy-scroll::-webkit-scrollbar{width:10px}
        .policy-scroll::-webkit-scrollbar-thumb{background:${rgba(accent, 0.45)};border-radius:999px;border:3px solid transparent;background-clip:padding-box}
        .policy-scroll::-webkit-scrollbar-thumb:hover{background:${rgba(accent, 0.7)};background-clip:padding-box}
        .policy-rich{color:${body}!important}
        .policy-rich *{color:inherit!important;border-color:${border}!important}
        .policy-rich a{color:${accent}!important;text-decoration:underline}
        .policy-rich h1,.policy-rich h2,.policy-rich h3,.policy-rich h4,.policy-rich h5,.policy-rich h6,.policy-rich strong,.policy-rich b{color:${ink}!important}
        .policy-rich :where(h1,h2,h3,h4){font-weight:700;margin:1.1em 0 .5em}
        .policy-rich p{margin:.7em 0;line-height:1.7}
        .policy-rich ul,.policy-rich ol{margin:.7em 0;padding-left:1.3em}
        .policy-rich li{margin:.35em 0;line-height:1.65}
        .policy-rich li::marker{color:${accent}}
      `}</style>
      <div
        className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[1.75rem] shadow-2xl sm:max-h-[86vh] sm:rounded-[1.75rem]"
        style={{ background: surface, border: `1px solid ${border}`, animation: "policyIn .34s cubic-bezier(.16,1,.3,1)", boxShadow: dark ? `0 30px 90px -20px rgba(0,0,0,.75)` : `0 30px 80px -26px ${rgba(accent, 0.4)}` }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* accent aura */}
        <div className="pointer-events-none absolute inset-x-0 -top-28 h-52 opacity-80 blur-3xl" style={{ background: `radial-gradient(58% 100% at 50% 0%, ${rgba(accent, dark ? 0.5 : 0.26)}, transparent 72%)` }} />
        {/* mobile grab handle */}
        <span className="absolute left-1/2 top-2.5 z-10 h-1.5 w-11 -translate-x-1/2 rounded-full sm:hidden" style={{ background: border }} />

        {/* header */}
        <div className="relative flex items-start gap-4 px-5 pb-5 pt-7 sm:px-8 sm:pt-7" style={{ borderBottom: `1px solid ${border}` }}>
          <span className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white" style={{ background: grad, boxShadow: `0 12px 28px ${rgba(accent, 0.45)}` }}>
            <Icon d={POLICY_ICON[kind] || POLICY_ICON.privacy} className="h-6 w-6" stroke={1.9} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[0.68rem] font-bold uppercase tracking-[0.18em]" style={{ color: accent }}>{kind === "terms" ? "Legal" : "Your privacy"}</p>
            <h3 className="mt-1 truncate text-xl font-extrabold sm:text-[1.35rem]" style={{ color: ink }}>{title}</h3>
          </div>
          <button onClick={onClose} aria-label="Close" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition hover:brightness-95" style={{ background: surface2, color: subtle, border: `1px solid ${border}` }}>
            <Icon d="M6 18L18 6M6 6l12 12" className="h-5 w-5" stroke={2} />
          </button>
        </div>

        {/* body */}
        <div className="policy-scroll overflow-y-auto px-5 py-6 sm:px-8">
          <div className="policy-rich max-w-none text-[15px]" style={{ color: body }} dangerouslySetInnerHTML={{ __html: html }} />
        </div>

        {/* footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 sm:px-8" style={{ borderTop: `1px solid ${border}`, background: surface2 }}>
          <span className="hidden text-xs sm:block" style={{ color: subtle }}>Last updated per community policy</span>
          <button onClick={onClose} className="ml-auto rounded-xl px-5 py-2.5 text-sm font-bold text-white transition hover:brightness-105 active:scale-[.98]" style={{ background: grad, boxShadow: `0 10px 24px ${rgba(accent, 0.4)}` }}>Got it</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ---------------------------- social footer set -------------------------- */
export const SOCIAL_ICONS = {
  facebook: "M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.9h-2.34V22c4.78-.79 8.44-4.94 8.44-9.94z",
  instagram: "M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z",
  youtube: "M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 00.5 6.2 31 31 0 000 12a31 31 0 00.5 5.8 3 3 0 002.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 002.1-2.1A31 31 0 0024 12a31 31 0 00-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z",
  x: "M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.4l-5.8-7.58-6.63 7.58H.49l8.6-9.83L0 1.15h7.59l5.24 6.93 6.07-6.93zm-1.29 19.5h2.04L6.49 3.24H4.3l13.31 17.41z",
  linkedin: "M19 0h-14C2.24 0 0 2.24 0 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5V5c0-2.76-2.24-5-5-5zM7.12 20.45H3.56V9h3.56v11.45zM5.34 7.43a2.06 2.06 0 110-4.12 2.06 2.06 0 010 4.12zM20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28z",
  whatsapp: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z",
};
